import { $color } from '../ui/color';
import { UISVGContent, $svg, UISVG } from '../ui/svg';
import { UIRect, UIPoint, UIRelPos } from '../ui/types';
import { CHXYPlot, CHXYAxes } from './axes';
import { CHColorMapper, CHScale } from './scale';

export interface CHGridPlotOptions {
    mapper: CHColorMapper;
}

export class CHGridPlot implements UISVGContent, CHXYPlot<number, number> {
    root: SVGGElement;
    axes: CHXYAxes<number, number> | undefined;

    private _data: number[][];
    private _rect?: UIRect;
    private _x_scale!: CHScale<number>;
    private _y_scale!: CHScale<number>;
    private _options: CHGridPlotOptions;
    private _enabled: boolean;

    constructor(data: number[][], options: CHGridPlotOptions) {
        this.root = $svg.G('ch-grid');

        this._data = data;
        this._enabled = true;
        this._options = options;
    }

    minMax(): [[number, number], [number, number]] {
        const data = this._data;

        return [[0, data.length], data.length ? [0, data[0].length] : [0, 0]];
    }

    layout(rect: UIRect, x_scale: CHScale<number>, y_scale: CHScale<number>) {
        const root = this.root;
        root.innerHTML = '';
        const data = this._data;
        const mapper = this._options.mapper;
        this._rect = rect;
        this._x_scale = x_scale;
        this._y_scale = y_scale;

        if(data.length < 1) return;

        this.root.setAttribute('transform', `translate(${rect[0]},${rect[1]})`);

        const w = rect[2] - rect[0];
        const h = rect[3] - rect[1];

        // const y0 = (1 - y_scale.normalize(0 as any)) * h;

        // const points: number[] = [];

        let max = 0;
        for(let i = 0; i < data.length; i++) {
            const d = data[i];
            for(let j = 0; j < d.length; j++) {
                if(d[j] > max)
                    max = d[j];
            }
        }

        for(let i = 0; i < data.length; i++) {
            const d = data[i];
            const y = Math.round((1 - y_scale.normalize(i + 0.5)) * h);
            const y1 = Math.round((1 - y_scale.normalize(i - 0.5)) * h);
            for(let j = 0; j < d.length; j++) {
                const v = d[j];

                const x = Math.round(x_scale.normalize(j - 0.5) * w);
                const x1 = Math.round(x_scale.normalize(j + 0.5) * w);

                const elem = $svg.RECT(x, y, x1 - x, y1 - y);
                elem.style.fill = $color.HTML(mapper.normalize(v / max))
                root.appendChild(elem);
            }
        }
    }

    hitTest(point: UIPoint | undefined) {
        if(point) {
            const w = this._rect![2] - this._rect![0];
            const h = this._rect![3] - this._rect![1];
            const x = Math.floor(this._x_scale.denormalize(point[0] / w));
            const y = Math.floor(this._y_scale.denormalize(1 - point[1] / h));

            const data = this._data;

            if(y < 0 || y >= data.length) return;
            if(x < 0 || x >= data[0].length) return;

            const x_norm = this._x_scale.normalize(x);
            const y_norm = this._y_scale.normalize(y);

            const p_x = x_norm * w;
            const p_y = (1 - y_norm) * h;

            return {
                distance: 0,
                text: data[y][x] + '',
                pos: [p_x + this._rect![0], p_y + this._rect![1]] as UIPoint,
                rel_pos: UIRelPos.Above
            }
        } else {
            return;
        }
    }

    render(svg: UISVG) {
        svg.add(this.root);
    }

    get enabled() {
        return this._enabled;
    }

    set enabled(e: boolean) {
        this._enabled = e;
        this.axes && this.axes.onPlotEnabled(this);
    }
}
